<template>
<div class="checkout-page">
  <div class="payment-information-column">
    <b>{{__('Customer info')}}</b>
    <input
      :placeholder="__('Email')"
      class="input-field"
      v-model="email"
      @change="startCheckEmail = true"
      @blur="startCheckEmail = email !== ''"
      />

    <transition name="slide-fade">
      <i v-if="!validEmail">{{__('Invalid email')}}</i>
    </transition>

    <br><br>

    <b>{{__('Delivery address')}}</b>
    <div class="city-wrapper">
      <input :placeholder="__('First name')" class="input-field" v-model="firstName" />
      <span />
      <input :placeholder="__('Surname')" class="input-field" v-model="lastName" />
    </div>
    <input :placeholder="__('Address')" class="input-field" v-model="address" />
    <div class="city-wrapper">
      <input :placeholder="__('City')" class="input-field" v-model="city" />
      <span />
      <input :placeholder="__('ZIPCode')" class="input-field" v-model="postCode" style="width: 120px;" />
    </div>
    <input :placeholder="__('Phone number')" class="input-field" v-model="phoneNumber" />
  </div>

  <div class="summary-wrapper">
    <checkout-summary
      :disabled="isMissingInfo"
      @purchase="purchase"
      />

    <br>
    <div class="loader-wrapper">
      <line-loader v-if="isPending"/>

      <i v-if="purchaseError">
        {{__('Something went wrong! Please check so all fields are correctly filled in.')}}
      </i>
    </div>
  </div>
</div>
</template>
<script>

import CheckoutSummary from 'components/orderPage/CheckoutSummary.vue'
import LineLoader from 'components/elements/LineLoader.vue'
import api from 'services/api.js'
import emailApi from 'services/emailApi.js'
import bugsnag from 'services/bugsnag.js'

import dataLog from 'services/dataLog.js'
import analytics from 'services/analytics.js'

import logScope from 'log-scope'
const log = logScope('InvoicePayment')

// Helper for computed construction
const computedMethod = attr => ({
  get () {
    return this.$store.getters[attr]
  },
  set (value) {
    this.$store.commit(attr, value)
  }
})

export default {
  components: { CheckoutSummary, LineLoader },
  data () {
    return {
      isPending: false,
      purchaseError: false,
      startCheckEmail: false
    }
  },
  computed: {
    quantity: computedMethod('quantity'),
    headQPrice: computedMethod('headQPrice'),
    shippingPrice: computedMethod('shippingPrice'),
    email: computedMethod('email'),
    firstName: computedMethod('firstName'),
    lastName: computedMethod('lastName'),
    address: computedMethod('address'),
    city: computedMethod('city'),
    postCode: computedMethod('postCode'),
    phoneNumber: computedMethod('phoneNumber'),
    validEmail () {
      if (this.startCheckEmail === false || this.email === '') {
        return true
      }

      return this.$store.getters.validEmail
    },
    isMissingInfo () {
      return !this.$store.getters.validPaymentInformation ||
        this.isPending === true
    }
  },
  methods: {
    async purchase ({ total, discount, couponCode }) {
      const partnerCode = this.$store.getters.partnerCode

      this.isPending = true
      this.purchaseError = false

      try {
        // Purchase
        await api.purchase({
          ...this.$store.getters.customerInformation,
          couponCode: couponCode,
          partnerCode: partnerCode,
          quantity: this.quantity
        })
        log.debug('Purchase completed')

        // Email verification
        await emailApi.sendOrderVerification({
          ...this.$store.getters.customerInformation,
          couponCode: couponCode,
          partnerCode: partnerCode,
          quantity: this.quantity,
          total: total
        })
        log.debug('Verification sent')

        // Log data
        dataLog.latest({
          status: 'success',
          message: 'New purchase!'
        })
        analytics.purchase()
        log.debug('Data logged')

        // Continue to next page
        this.nextPage()
      } catch (err) {
        console.log(err)
        this.purchaseError = true

        bugsnag.notify(err, {
          ...this.$store.getters.customerInformation,
          couponCode: couponCode,
          partnerCode: partnerCode,
          quantity: this.quantity
        })
      }

      this.isPending = false
    },
    nextPage () {
      this.$router.push({ name: 'HomePage' })
      this.$router.push({ name: 'OrderPage' })
      this.$router.push({ name: 'HomePage' })
      this.$router.push({ name: 'PostPurchasePage' })
    }
  }
}
</script>
<style lang="less" scoped>
  @import '../../style/variables.less';

  /* Shared child classes */
  .checkout-page {
    background-color: #ffffff;
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    height: auto;
    width: 100%;
    margin-bottom: 100px;

    @media (max-width: 800px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
    }
  }

  .payment-information-column {
    width: 100%;
    max-width: 500px;
    box-sizing: border-box;
    padding-right: 20px;

    i {
      color: @warning-dark;
      font-size: 14px;
    }

    @media (max-width: 800px) {
      padding-right: 0px;
      max-width: 400px;
    }
  }

  .summary-wrapper {
    width: 100%;
    max-width: 300px;
    box-sizing: border-box;
    padding-left: 20px;

    @media (max-width: 800px) {
      padding-left: 0px;
      max-width: 400px;
      margin: 50px 0px;
    }

    .loader-wrapper {
      height: 20px;
      width: 100%;

      i {
        color: @warning-dark;
        font-size: 14px;
      }
    }
  }

  .city-wrapper {
    display: flex;
    flex-direction: row;

    span {
      width: 10px;
      min-width: 10px;
    }
  }

  .input-field {
    border-radius: 5px;
    border: none;
    box-sizing: border-box;
    box-shadow: inset 0px 2px 4px rgba(0, 0, 0, 0.2);
    height: 50px;
    padding-left: 20px;
    width: 100%;
    margin-bottom: 10px;
  }

  .slide-fade-enter-active {
    transition: all .5s ease;
  }

  .slide-fade-leave-active {
    transition: none;
  }

  .slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
    transform: translateY(-10px);
    opacity: 0;
  }
</style>
